import { useEffect, useRef } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';

import { restoreOptimisticAgentReplies } from './optimisticAgentReplyReconciliation.ts';
import {
	useReactionRefetchRequestTracker,
	type ReactionRefetchRequestTracker,
} from './useReactionRefetchRequestTracker.ts';

import type { ConversationState } from './useCommentAgentSession.ts';

// The popup reads immediately on mount; state transitions wait for the first backend write.
const REACTIONS_TRANSITION_REFETCH_DELAYS_MS: number[] = [2000, 6000, 14000];
const REACTIONS_POPUP_REFETCH_DELAYS_MS: number[] = [0, 2000, 6000, 14000];

type VisibleReactionsSummary = {
	reactionsCount: number;
	reactionsSummaryForEmoji: ReadonlyArray<{
		emojiId: string;
		count: number;
		reacted: boolean;
	} | null>;
} | null;

const visibleReactionSignature = (summary: VisibleReactionsSummary | undefined): string => {
	const groups: string[] = [];
	for (const reaction of summary?.reactionsSummaryForEmoji ?? []) {
		if (reaction) {
			groups.push(JSON.stringify([reaction.emojiId, reaction.count, reaction.reacted]));
		}
	}
	return JSON.stringify([summary?.reactionsCount ?? 0, groups.sort()]);
};

type UseRefetchReactionsOnAgentSessionChangeProps = {
	commentId: string | undefined;
	contentId: string;
	agentSessionState: ConversationState | 'THINKING' | null;
	displayedReactionsSummary?: VisibleReactionsSummary;
	refetchOnMount?: boolean;
	requestTracker?: ReactionRefetchRequestTracker;
	onRefetch?: (comment: Awaited<ReturnType<typeof fetchCommentById>>) => void;
};

/**
 * The agent writes/removes its own reaction on the comment as it works (Reaction Service),
 * with no pubsub reaching the client for that write. This hook piggybacks on the
 * `useCommentAgentSession` status signal that already drives the comment's status row: each
 * transition (WORKING/ARTEFACT_READY/INPUT_REQUIRED/FAILED/null) means a reaction write may have
 * just landed, so it refetches this comment's reactions.
 *
 * Fires a bounded round of refetches per transition rather than debouncing them, so the
 * reactions never lag behind the status row, which itself updates with no delay. Failures are
 * swallowed — this is a best-effort refresh, not a source of truth, so a failed attempt just
 * leaves the last-known reactions in place.
 */
export function useRefetchReactionsOnAgentSessionChange({
	commentId,
	contentId,
	agentSessionState,
	displayedReactionsSummary,
	refetchOnMount = false,
	onRefetch,
	requestTracker,
}: UseRefetchReactionsOnAgentSessionChangeProps): void {
	const privateTracker = useReactionRefetchRequestTracker('', contentId, commentId);
	const tracker = requestTracker ?? privateTracker;
	const previousAgentSessionStateRef = useRef(refetchOnMount ? null : agentSessionState);
	const isInitialMountRef = useRef(refetchOnMount);
	const refetchTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const onRefetchRef = useRef(onRefetch);
	onRefetchRef.current = onRefetch;
	const displayedReactionsSummaryRef = useRef(displayedReactionsSummary);
	displayedReactionsSummaryRef.current = displayedReactionsSummary;

	useEffect(() => {
		const isPopupMount = isInitialMountRef.current;
		isInitialMountRef.current = false;
		if (agentSessionState === previousAgentSessionStateRef.current) {
			return;
		}
		previousAgentSessionStateRef.current = agentSessionState;

		// Supersede any in-flight round from the previous transition so timers don't pile up.
		refetchTimersRef.current.forEach(clearTimeout);
		refetchTimersRef.current = [];

		if (!commentId || !expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
			return;
		}

		const delays = isPopupMount
			? REACTIONS_POPUP_REFETCH_DELAYS_MS
			: REACTIONS_TRANSITION_REFETCH_DELAYS_MS;
		const roundStartedAt = Date.now();
		let cancelled = false;
		let changed = false;
		const scheduleAttempt = (index: number) => {
			const delay = Math.max(0, delays[index] - (Date.now() - roundStartedAt));
			refetchTimersRef.current = [
				setTimeout(() => {
					const token = tracker.issue();
					void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
						.then((comment) => {
							// The response also replaces this thread's replies.
							restoreOptimisticAgentReplies();
							if (comment && !cancelled && tracker.acceptResponse(token)) {
								changed =
									tracker.isLatest(token) &&
									visibleReactionSignature(comment.reactionsSummary) !==
										visibleReactionSignature(displayedReactionsSummaryRef.current);
								onRefetchRef.current?.(comment);
							}
						})
						.catch(() => {})
						.finally(() => {
							if (!cancelled && !changed && index + 1 < delays.length) {
								scheduleAttempt(index + 1);
							}
						});
				}, delay),
			];
		};
		scheduleAttempt(0);

		return () => {
			cancelled = true;
			refetchTimersRef.current.forEach(clearTimeout);
			refetchTimersRef.current = [];
		};
	}, [agentSessionState, commentId, contentId, tracker]);
}
